import { FunctionComponent, useCallback, useMemo } from "react";
import { BetslipType } from "../../../constants";
import { ModalBetslipProps } from "./ModalBetslip.types";
import { i18n } from "../../../helpers/i18n";
import ConnectedConfirmationDrawer from "../../ConfirmationDrawer";
import { ConfirmationDrawer } from "../../ConfirmationDrawer/ConfirmationDrawer.web";
import { BetslipDrawer } from "../snowflakes/BetslipDrawer/BetslipDrawer.web";
import ConnectedCollapsedView from "../snowflakes/CollapsedView/index";
import { CollapsedView } from "../snowflakes/CollapsedView/CollapsedView.web";
import ConnectedQuickBetslipView from "../snowflakes/QuickBetslipView/index";
import { QuickBetslipView } from "../snowflakes/QuickBetslipView/QuickBetslipView.web";
import { useQuickBetslipPresentation } from "../useQuickBetslipPresentation";

export const ModalBetslip: FunctionComponent<ModalBetslipProps & { isReceiptReusePending: boolean }> = ({
  quickBetslipBet,
  isReceiptReusePending,
  hasMultiples,
  hasConfirmation,
  activeProduct,
  step,
  isClosed,
  isCollapsed,
  activeBetslipType,
  dispatchHeaderToggle,
  dispatchDismissClick,
}) => {
  const { combinationId, shouldShowQuickBetslip } = useQuickBetslipPresentation(quickBetslipBet, step === "REPORT");
  const visibleStep = isReceiptReusePending ? "REPORT" : step;

  const handleHeaderToggle = useCallback(() => {
    if (visibleStep === "REPORT") {
      dispatchDismissClick();
      return;
    }

    const betslipSubType = hasMultiples ? "SPORTSBOOK_MULTIPLES" : "SPORTSBOOK_SINGLES";

    dispatchHeaderToggle(isCollapsed, betslipSubType);
  }, [visibleStep, hasMultiples, dispatchHeaderToggle, isCollapsed, dispatchDismissClick]);

  const title = useMemo(() => {
    if (visibleStep === "REPORT") {
      return i18n({ key: "I18N.BETSLIP.RECEIPT_TITLE" });
    }

    if (activeBetslipType === BetslipType.OBB) {
      return i18n({ key: "I18N.OBB_BETSLIP.TITLE" });
    }

    return visibleStep === "CONFIRM_POTENTIAL"
      ? i18n({ key: "I18N.BETSLIP.REVIEW_AND_CONFIRM_BET" })
      : i18n({ key: "I18N.BETSLIP.TITLE" });
  }, [visibleStep, activeBetslipType]);

  const masterBetslip = useMemo(() => {
    return (
      <>
        {isCollapsed && !isReceiptReusePending ? (
          <ConnectedCollapsedView component={CollapsedView} hasMultiples={hasMultiples} onClick={handleHeaderToggle} />
        ) : (
          <BetslipDrawer
            title={title}
            step={visibleStep}
            activeBetslipType={activeBetslipType}
            onClose={handleHeaderToggle}
          />
        )}
        {hasConfirmation && <ConnectedConfirmationDrawer component={ConfirmationDrawer} />}
      </>
    );
  }, [
    isCollapsed,
    isReceiptReusePending,
    hasMultiples,
    handleHeaderToggle,
    title,
    visibleStep,
    activeBetslipType,
    hasConfirmation,
  ]);

  if (isClosed || activeProduct !== "SPORTSBOOK") {
    return null;
  }

  if (shouldShowQuickBetslip && isCollapsed && !isReceiptReusePending && step !== "REPORT" && combinationId) {
    return (
      <>
        <ConnectedQuickBetslipView
          component={QuickBetslipView}
          combinationId={combinationId}
          onClick={handleHeaderToggle}
        />
        {hasConfirmation && <ConnectedConfirmationDrawer component={ConfirmationDrawer} />}
      </>
    );
  }

  return masterBetslip;
};
